import {
  AdministrationList, Badge, Button, Card, DateInput, ErrorState, Field, Input, LoadingState, Permissions, Row, Select,
  Stack, Textarea,
  invalidDateIn, useApi, useI18n, usePermission, type MedicationResponse,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

/**
 * A child's medication, entered by the administration as the family told them (Q17).
 *
 * Guardians enter nothing. The form is an option of the tenant's `MedicationForm` catalogue, so a nursery adds
 * one in its settings rather than waiting for a release; the dose stays text and is shown with the form's unit.
 * Reading needs a health permission, since the list is health data (SRS §2.2); entering needs `health.manage`.
 */
export function MedicationsCard({ childId }: { childId: number }) {
  const api = useApi();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const canManage = usePermission(Permissions.health.manage);
  const canView = usePermission(Permissions.health.view) || canManage;
  const [adding, setAdding] = useState(false);

  const key = ['medications', childId];
  const medications = useQuery({
    queryKey: key,
    queryFn: () => api.health.medications(childId),
    enabled: canView,
  });

  if (!canView) return null;

  return (
    <Card
      title={t('medication.title')}
      hint={t('medication.hint')}
      action={canManage && !adding
        ? <Button variant="secondary" onClick={() => setAdding(true)}>{t('medication.authorize')}</Button>
        : undefined}
    >
      <Stack>
        {adding && (
          <AddMedicationForm
            childId={childId}
            onCancel={() => setAdding(false)}
            onAdded={() => {
              setAdding(false);
              void queryClient.invalidateQueries({ queryKey: key });
            }}
          />
        )}

        {medications.isPending && <LoadingState />}
        {medications.isError && <ErrorState error={medications.error} onRetry={() => void medications.refetch()} />}
        {medications.data?.length === 0 && <p className="dg-card__hint">{t('medication.empty')}</p>}
        {medications.data?.map((medication) => <MedicationRow key={medication.id} medication={medication} />)}
      </Stack>
    </Card>
  );
}

function MedicationRow({ medication }: { medication: MedicationResponse }) {
  const { t, formatDate, formatNumber, locale } = useI18n();

  const form = (locale === 'en' && medication.formNameEn) || medication.formNameAr;
  const unit = (locale === 'en' ? medication.unitEn : medication.unitAr) ?? medication.unitAr;
  const dose = unit ? t('medication.doseWithUnit', { dose: medication.dose, unit }) : medication.dose;

  return (
    <Stack tight>
      <Row between>
        <span className="dg-stack dg-stack--tight">
          <strong>{medication.medicationName}</strong>
          <span className="dg-row">
            <Badge>{form}</Badge>
            <span>{dose}</span>
            <span className="dg-card__hint">{medication.schedule}</span>
          </span>
          <span className="dg-row">
            <span className="dg-card__hint">
              {medication.endDate
                ? t('medication.period', { from: formatDate(medication.startDate), to: formatDate(medication.endDate) })
                : t('medication.since', { from: formatDate(medication.startDate) })}
            </span>
            {medication.informedByName && (
              <span className="dg-card__hint">{t('medication.informedByName', { name: medication.informedByName })}</span>
            )}
            {medication.recordedByName && (
              <span className="dg-card__hint">{t('medication.recordedBy', { name: medication.recordedByName })}</span>
            )}
          </span>
        </span>
        <span className="dg-row">
          <Badge tone={medication.isCurrent ? 'success' : 'neutral'}>
            {medication.isCurrent ? t('medication.current') : t('medication.ended')}
          </Badge>
          {medication.isCurrent && <span>{t('medication.dosesToday', { count: formatNumber(medication.dosesToday) })}</span>}
        </span>
      </Row>
      {/* What was given, by whom and when (FR-CHD-16), inside the reader's history window. */}
      <AdministrationList medication={medication} administrations={medication.administrations} />
    </Stack>
  );
}

function AddMedicationForm({ childId, onCancel, onAdded }: {
  childId: number;
  onCancel: () => void;
  onAdded: () => void;
}) {
  const api = useApi();
  const { t, locale } = useI18n();

  const [name, setName] = useState('');
  const [formId, setFormId] = useState<number | ''>('');
  const [dose, setDose] = useState('');
  const [schedule, setSchedule] = useState('');
  const [maxPerDay, setMaxPerDay] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [informedBy, setInformedBy] = useState<number | ''>('');
  const [incomplete, setIncomplete] = useState(false);

  const forms = useQuery({
    queryKey: ['catalog', 'MedicationForm'],
    queryFn: () => api.catalog.list({ type: 'MedicationForm' }),
  });
  const guardians = useQuery({
    queryKey: ['guardians', childId],
    queryFn: () => api.guardians.forChild(childId),
  });

  const add = useMutation({
    mutationFn: () => api.health.authorizeMedication(childId, {
      medicationName: name.trim(),
      dose: dose.trim(),
      formId: Number(formId),
      schedule: schedule.trim(),
      maxDosesPerDay: maxPerDay ? Number(maxPerDay) : null,
      startDate: startDate || null,
      endDate: endDate || null,
      notes: notes.trim() || null,
      informedByGuardianMembershipId: informedBy === '' ? null : informedBy,
    }),
    onSuccess: onAdded,
  });

  const unitOf = (id: number | '') => {
    const chosen = forms.data?.find((form) => form.id === id);
    return (locale === 'en' ? chosen?.unitEn : chosen?.unitAr) ?? chosen?.unitAr ?? null;
  };

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const unreadable = invalidDateIn(event.currentTarget);
    if (unreadable) { unreadable.focus(); return; }
    const complete = Boolean(name.trim() && dose.trim() && schedule.trim() && formId !== '');
    setIncomplete(!complete);
    if (complete) add.mutate();
  }

  const unit = unitOf(formId);

  return (
    <form onSubmit={submit} noValidate>
      <Stack>
        {add.isError && <ErrorState error={add.error} />}
        {incomplete && <p className="dg-field__error" role="alert">{t('medication.required')}</p>}

        <Field label={t('medication.name')} required>
          {(props) => <Input {...props} value={name} onChange={(event) => setName(event.target.value)} autoFocus />}
        </Field>

        <Row>
          <Field label={t('medication.form')} required>
            {(props) => (
              <Select
                {...props}
                value={formId}
                onChange={(event) => setFormId(event.target.value === '' ? '' : Number(event.target.value))}
              >
                <option value="">{t('medication.form.choose')}</option>
                {(forms.data ?? []).map((form) => (
                  <option key={form.id} value={form.id}>
                    {(locale === 'en' && form.nameEn) || form.nameAr}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={unit ? t('medication.doseIn', { unit }) : t('medication.dose')} hint={t('medication.dose.hint')} required>
            {(props) => <Input {...props} value={dose} onChange={(event) => setDose(event.target.value)} />}
          </Field>
        </Row>

        <Row>
          <Field label={t('medication.schedule')} required>
            {(props) => <Input {...props} value={schedule} onChange={(event) => setSchedule(event.target.value)} />}
          </Field>
          <Field label={t('medication.maxPerDay')}>
            {(props) => (
              <Input {...props} type="number" min={1} inputMode="numeric" value={maxPerDay}
                onChange={(event) => setMaxPerDay(event.target.value)} />
            )}
          </Field>
        </Row>

        <Row>
          <Field label={t('medication.startDate')}>
            {(props) => <DateInput {...props} value={startDate} onChange={setStartDate} />}
          </Field>
          <Field label={t('medication.endDate')}>
            {(props) => <DateInput {...props} value={endDate} onChange={setEndDate} />}
          </Field>
        </Row>

        <Field label={t('medication.informedBy')}>
          {(props) => (
            <Select
              {...props}
              value={informedBy}
              onChange={(event) => setInformedBy(event.target.value === '' ? '' : Number(event.target.value))}
            >
              <option value="">{t('medication.informedBy.none')}</option>
              {(guardians.data ?? []).map((guardian) => (
                <option key={guardian.guardianMembershipId} value={guardian.guardianMembershipId}>
                  {guardian.fullName ?? guardian.phone}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label={t('medication.notes')}>
          {(props) => <Textarea {...props} value={notes} onChange={(event) => setNotes(event.target.value)} />}
        </Field>

        <Row>
          <Button type="submit" busy={add.isPending} busyLabel={t('action.saving')}>{t('medication.authorize')}</Button>
          <Button variant="ghost" onClick={onCancel}>{t('action.cancel')}</Button>
        </Row>
      </Stack>
    </form>
  );
}
